/* eslint-disable no-template-curly-in-string */
import { React, PT, fetchApi, withRouter } from '@gasbuddy/react';
import { Button, Modal, Header, Form, Icon, Popup, Message } from 'semantic-ui-react';

function validTemplate(template, provider, buttonProvider) {
  if (provider === buttonProvider) {
    return null;
  }
  if (!template || template.indexOf('${affiliateAccountId}') < 0) {
    return 'The template is required and must have the account placeholder in it';
  }
  return null;
}

function merchantOptions(merchants, newMerchant) {
  const values = (merchants || []).map(m => ({
    text: `${m.internal_name} (${m.offers} offers)`,
    value: m.internal_name,
  }));
  if (newMerchant) {
    values.push({ text: `${newMerchant} (new)`, value: newMerchant });
  }
  return values;
}

function defaultState(props) {
  const state = {
    opened: !!props.open,
  };
  if (props?.providers?.length === 1) {
    state.provider = props.providers[0].affiliate_provider_id;
  }
  if (props?.merchantId) {
    const merchant = props.merchants.find(m => m.merchant_id === props.merchantId);
    state.internalName = merchant?.internal_name;
    state.displayName = merchant?.display_name;
  }
  return state;
}

class BaseCreateOfferModal extends React.Component {
  state = defaultState(this.props)

  onChange = (f, v) => { this.setState({ [f]: v }); }

  saveVariant = () => {
    const { history, providers } = this.props;
    const buttonProvider = providers?.find(p => p.name === 'button')?.affiliate_provider_id;
    const {
      linkPath, internalName, displayName, lockinDays, gbCommission = '',
      reward, rewardType, provider, providerIdentifier, providerMerchantIdentifier, template, imageName,
      baseOfferId, restrict,
    } = this.state;
    const errors = [
      linkPath ? null : 'Link Path is required',
      internalName ? null : 'Merchant Key is required',
      reward ? null : 'Reward value is required',
      rewardType ? null : 'Reward Type is required',
      imageName ? null : 'Image name is required',
      Number.isNaN(Number(reward)) ? 'A valid reward value is required' : null,
      validTemplate(template, provider, buttonProvider),
      provider === buttonProvider && !gbCommission ? 'GasBuddy commission information is required' : null,
      (provider === buttonProvider && (!lockinDays || Number.isNaN(Number(lockinDays)))) ? 'Button lockin period is required and must be numeric' : null,
      (provider === buttonProvider && !gbCommission.startsWith('$') && !gbCommission.endsWith('%')) ? 'GasBuddy commission must start with $ or end with %' : null,
    ].filter(i => i);
    if (errors.length) {
      this.setState({ error: errors });
      return;
    }
    const restrictEligibilityQueryMap = {
      premium_only: 'membership.plan_offering.affiliate_bonus_program == "bonus"',
      exclude_premium: 'membership.plan_offering.affiliate_bonus_program != "bonus"',
    };
    const newOffer = {
      linkPath,
      internalName,
      displayName,
      reward,
      rewardType,
      provider,
      providerIdentifier,
      providerMerchantIdentifier,
      imageName,
      baseOfferId,
      eligibility: restrictEligibilityQueryMap[restrict],
    };
    if (provider === buttonProvider) {
      newOffer.lockinDays = lockinDays;
      newOffer.gbCommission = gbCommission;
    } else {
      newOffer.template = template;
    }
    this.setState({ loading: true, error: null }, async () => {
      const { status, body } = await fetchApi({
        url: '/loyalty/offers/create',
        body: newOffer,
      });
      if (status !== 200) {
        this.setState({ loading: false, error: body });
      } else {
        this.setState({
          loading: false,
          opened: false,
        });
        history.push(`/loyalty/offers/${body.offerId}`);
      }
    });
  }

  setMerchantValue = (e, { value }) => {
    const { merchants } = this.props;
    const newState = {
      internalName: value,
    };
    const mrec = merchants.find(i => i.internal_name === value);
    if (!mrec) {
      newState.newMerchant = value;
    } else {
      newState.displayName = mrec.display_name;
    }
    this.setState(newState);
  }

  setLinkPath = (e, { value }) => {
    const { imageName, linkPath } = this.state;
    const newState = { linkPath: value };
    if (!imageName || imageName === linkPath) {
      newState.imageName = value;
    }
    this.setState(newState);
  }

  render() {
    const { providers, merchants } = this.props;
    const {
      opened, error, linkPath, internalName, displayName, loading, newMerchant, gbCommission,
      reward, rewardType, provider, providerIdentifier, providerMerchantIdentifier, template, imageName, lockinDays,
      baseOfferId, restrict,
    } = this.state;
    const restrictionText = {
      premium_only: 'Allow Premium Users Only',
      exclude_premium: 'Allow Non-Premium Users Only',
    };
    const buttonProvider = providers?.find(p => p.name === 'button')?.affiliate_provider_id;
    return (
      <Modal
        open={opened}
        trigger={(
          <Button
            onClick={() => this.setState({ opened: true })}
            content="Create New Offer"
            color="green"
            icon="plus"
            labelPosition="left"
            floated="right"
          />
        )}
      >
        <Header content="Create New Offer" />
        <Modal.Content>
          {error
            && (
            <Message error>
              Unable to create offer:
              <pre>{JSON.stringify(error, null, '\t')}</pre>
            </Message>
            )
          }
          <p>
            An offer is a binding of a merchant to an affiliate provider (optional) and a set of
            offer terms, optionally exposed to the user via a link. You must setup the immutable
            parts (reward type, amounts, merchant name) here in magenta, and then you can edit
            the text content and images in Contentful.
          </p>
          <Form loading={loading}>
            <Form.Group widths="equal">
              <Popup
                wide
                trigger={(
                  <Form.Input
                    label="Link Path"
                    name="linkPath"
                    value={linkPath}
                    onChange={this.setLinkPath}
                    placeholder="Link Name (Unique)"
                  />
                )}
                content={(
                  <p>
                    The link path is what will be used in any email marketing
                    for this offer. Technically you can have more than one link path
                    per offer, but you need at least one to start. The link path is also
                    the name of the entry in Contentful. This path must be unique across
                    all offers.
                  </p>
                )}
              />
              <Popup
                wide
                trigger={(
                  <Form.Dropdown
                    search
                    selection
                    allowAdditions
                    label="Merchant Key"
                    name="internalName"
                    text={internalName || 'Merchant Internal Name (Unique)'}
                    value={internalName}
                    options={merchantOptions(merchants, newMerchant)}
                    onAddItem={this.setMerchantValue}
                    onChange={this.setMerchantValue}
                  />
                )}
                content={(
                  <p>
                    The internal name of the merchant is what we use to refer to the merchant
                    in reporting, and is typically the same as the link path (unless we&apos;ve already
                    run an offer from this same merchant). If the name you enter already exists
                    we will just reuse that merchant. Internal name should be lower case and not
                    have spaces. You can use dashes instead of spaces. This helps avoid accidental
                    duplicates.
                  </p>
                )}
              />
              <Popup
                wide
                trigger={(
                  <Form.Input
                    label="Merchant Name"
                    name="displayName"
                    value={displayName}
                    onChange={(e, { value }) => this.onChange('displayName', value)}
                    placeholder="User Visible Merchant Name"
                  />
                  )}
                content={(
                  <p>
                    The properly capitalized and spaced name of the merchant, suitable
                    for a human to read. Does not need to be unique across all merchants.
                  </p>
                )}
              />
              <Popup
                wide
                trigger={(
                  <Form.Input
                    label="Provider Merchant Id"
                    name="providerMerchantIdentifier"
                    value={providerMerchantIdentifier}
                    onChange={(e, { value }) => this.onChange('providerMerchantIdentifier', value)}
                    placeholder="Provider MID"
                  />
                  )}
                content={(
                  <p>
                    If the affiliate provider selected has an identifier for
                    <b> this merchant </b>
                    (not for the campaign, but the merchant), enter it here.
                  </p>
                )}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Dropdown
                selection
                label="Reward Type"
                text={rewardType || 'Reward is a...'}
                value={rewardType}
                options={[
                  { text: 'Fixed Amount', value: 'flat' },
                  { text: 'Percentage of Order', value: 'percent' },
                  { text: 'Percentage of Commission', value: 'percent_commission' },
                  { text: 'Variable Percentage', value: 'variable_percent' },
                  { text: 'Variable Amount', value: 'variable_flat' },
                ]}
                onChange={(e, { value }) => this.onChange('rewardType', value)}
              />
              <Form.Input
                label="Amount"
                name="reward"
                value={reward}
                type="number"
                icon={rewardType === 'flat' ? 'dollar sign' : 'percent'}
                iconPosition={rewardType === 'flat' ? 'left' : 'right'}
                onChange={(e, { value }) => this.onChange('reward', value)}
              />
              <Form.Dropdown
                selection
                label="Affiliate Provider"
                placeholder="(Optional)"
                value={provider}
                options={(providers || []).map(p => ({ value: p.affiliate_provider_id, text: p.name }))}
                onChange={(e, { value }) => this.onChange('provider', value)}
              />
              <Popup
                wide
                trigger={(
                  <Form.Input
                    label="Provider Campaign Id"
                    name="providerIdentifier"
                    value={providerIdentifier}
                    onChange={(e, { value }) => this.onChange('providerIdentifier', value)}
                  />
                  )}
                content={(
                  <p>
                    If the affiliate sends us a feed of transactions, they often have a specific
                    identifier for this offer. Enter that value here so we can tie transactions
                    back to this merchant offer.
                  </p>
                )}
              />
            </Form.Group>
            <Form.Group>
              <Popup
                wide
                trigger={(
                  <Form.Input
                    width={4}
                    label="Image Name"
                    name="imageName"
                    value={imageName}
                    onChange={(e, { value }) => this.onChange('imageName', value)}
                  />
                )}
                content={(
                  <p>
                    Typically the same as the merchant key. Do not enter a full url
                    or file extension. Just the name of the image that should be used.
                    You can use Contentful to upload the images.
                  </p>
                )}
              />
            </Form.Group>
            <Form.Group>
              <Popup
                wide
                trigger={(
                  <Form.Input
                    width={4}
                    label="Base Offer Id"
                    name="baseOfferId"
                    value={baseOfferId}
                    onChange={(e, { value }) => this.onChange('baseOfferId', value)}
                  />
                )}
                content={(
                  <p>
                    The offerId of an existing offer that will share it&#39s CMS content
                    with this newly created offer. For example, as the premium/non-premium
                    variants of the same logical offer.
                  </p>
                )}
              />
              <Form.Dropdown
                selection
                label="Restriction"
                text={restrictionText[restrict] || 'Allow All Users'}
                value={restrict || ''}
                options={[
                  { text: 'Allow All Users', value: '' },
                  ...Object.entries(restrictionText).map(([k, v]) => ({ text: v, value: k })),
                ]}
                onChange={(e, { value }) => this.onChange('restrict', value)}
              />
              {provider !== buttonProvider && (
                <Popup
                  wide
                  trigger={(
                    <Form.Input
                      width={12}
                      label="Partner Link Template"
                      name="template"
                      value={template}
                      onChange={(e, { value }) => this.onChange('template', value)}
                      // eslint-disable-next-line no-template-curly-in-string
                      placeholder={'https://somewhere.com/${affiliateAccountId}'}
                    />
                    )}
                  content={(
                    <p>
                      This is the link to which the user is sent to complete the transaction.
                      It is absolutely crucial that you get this right, and that it include the
                      &quot;placeholder&quot; for our account identifier, which is
                      <i>{' ${affiliateAccountId}'}</i>
                      . For
                      {' '}
                      <b>Button Merchants</b>
                      {' '}
                      leave this blank.
                    </p>
                  )}
                />
              )}
              {provider === buttonProvider && (
                <React.Fragment>
                  <Popup
                    wide
                    trigger={(
                      <Form.Input
                        width={8}
                        label="Lock-in Days"
                        name="lockinDays"
                        value={lockinDays}
                        type="number"
                        onChange={(e, { value }) => this.onChange('lockinDays', value)}
                        placeholder="# Days"
                      />
                      )}
                    content={(
                      <p>
                        Button doesn&apos;t currently tell us how long it will take for the
                        offer to &quot;lock in&quot; so that we can award final GasBack. Talk
                        to Kelly at Button to get a value for this until their API can tell us
                        instead.
                      </p>
                    )}
                  />
                  <Popup
                    wide
                    trigger={(
                      <Form.Input
                        width={8}
                        label="GasBuddy Commission"
                        name="gbCommission"
                        value={gbCommission}
                        onChange={(e, { value }) => this.onChange('gbCommission', value)}
                        placeholder="$x or x%"
                      />
                    )}
                    content={(
                      <p>
                        Because of personalized offers from Button, we MUST
                        know the amount Button plans on paying GasBuddy. You must
                        either prefix the amount with a $ if it&apos;s a flat amount,
                        or use a percent sign suffix.
                      </p>
                    )}
                  />
                </React.Fragment>
              )}
            </Form.Group>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="grey" onClick={() => this.setState({ opened: false })}>
            <Icon name="minus" />
            {' '}
            Cancel
          </Button>
          <Button basic color="red" onClick={this.saveVariant}>
            <Icon name="plus" />
            {' '}
            Create Offer
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

BaseCreateOfferModal.propTypes = {
  history: PT.shape({ push: PT.func }).isRequired,
  providers: PT.arrayOf(PT.shape({ affiliate_provider_id: PT.number, name: PT.string })).isRequired,
  merchants: PT.arrayOf(PT.shape({ internal_name: PT.string, offers: PT.number })).isRequired,
};

export const CreateOfferModal = withRouter(BaseCreateOfferModal);
